import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { siteApi } from "../lib/api";
import {
  GAME_MINI_PLAYER_EVENT,
  readStoredMiniGamePlayer,
} from "../lib/gameLaunch";
import { WALLET_SYNC_EVENT } from "../utils/walletSync";

const FOREGROUND_REFRESH_MS = 10000;
const GAME_REFRESH_MS = 30000;
const WALLET_POLL_LOCK_KEY = "crypto-wallet-summary-poll-lock";
const WALLET_POLL_LOCK_TTL_MS = 15000;

const readPollLock = () => {
  try {
    const raw = localStorage.getItem(WALLET_POLL_LOCK_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const canPollInThisTab = (ownerId) => {
  const lock = readPollLock();
  if (lock && lock.ownerId !== ownerId && Number(lock.expiresAt || 0) > Date.now()) {
    return false;
  }
  try {
    localStorage.setItem(
      WALLET_POLL_LOCK_KEY,
      JSON.stringify({ ownerId, expiresAt: Date.now() + WALLET_POLL_LOCK_TTL_MS })
    );
  } catch {
    // No storage means no cross-tab coordination, but polling can continue.
  }
  return true;
};

const extractWalletSummary = (payload) => {
  const source = payload?.data?.user || payload?.data || payload?.user || payload || {};
  return source.wallet || source;
};

const readBalance = (summary) => {
  const value =
    summary?.available_balance ??
    summary?.balance ??
    summary?.wallet?.available_balance ??
    summary?.wallet?.balance;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const WalletContext = createContext(null);

export const WalletProvider = ({ children }) => {
  const location = useLocation();
  const { user, updateBalance, updateUser } = useAuth();
  const [walletSummary, setWalletSummary] = useState(() =>
    extractWalletSummary(user?.profile)
  );
  const requestRef = useRef(null);
  const timerRef = useRef(null);
  const walletSummaryRef = useRef(walletSummary);
  const profileWalletRef = useRef(user?.profile?.wallet || {});
  const tabIdRef = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [isMiniGameActive, setIsMiniGameActive] = useState(() =>
    Boolean(readStoredMiniGamePlayer()?.active)
  );

  const applyWalletSummary = useCallback(
    (...payloads) => {
      const next = payloads.map(extractWalletSummary).find((item) => item && Object.keys(item).length);
      if (!next) return null;
      setWalletSummary(next);
      const balance = readBalance(next);
      if (balance !== null) updateBalance(balance);
      updateUser({
        wallet: { ...profileWalletRef.current, ...next },
        ...(balance !== null ? { balance } : {}),
      });
      return next;
    },
    [updateBalance, updateUser]
  );

  const refreshWalletSummary = useCallback(async ({ force = false } = {}) => {
    if (!user?.isAuthenticated) return null;
    if (!force && document.visibilityState !== "visible") return walletSummaryRef.current;
    if (!force && !canPollInThisTab(tabIdRef.current)) return walletSummaryRef.current;
    if (requestRef.current) return requestRef.current;

    requestRef.current = siteApi
      .getWalletSummary()
      .then((response) =>
        response?.success === false
          ? walletSummaryRef.current
          : applyWalletSummary(response?.data || response)
      )
      .finally(() => {
        requestRef.current = null;
      });
    return requestRef.current;
  }, [applyWalletSummary, user?.isAuthenticated]);

  useEffect(() => {
    setWalletSummary(extractWalletSummary(user?.profile));
  }, [user?.profile]);

  useEffect(() => {
    walletSummaryRef.current = walletSummary;
  }, [walletSummary]);

  useEffect(() => {
    profileWalletRef.current = user?.profile?.wallet || {};
  }, [user?.profile?.wallet]);

  useEffect(() => {
    const syncMiniState = () => {
      setIsMiniGameActive(Boolean(readStoredMiniGamePlayer()?.active));
    };
    window.addEventListener(GAME_MINI_PLAYER_EVENT, syncMiniState);
    return () => window.removeEventListener(GAME_MINI_PLAYER_EVENT, syncMiniState);
  }, []);

  useEffect(() => {
    if (!user?.isAuthenticated) return undefined;
    let disposed = false;

    const schedule = () => {
      window.clearTimeout(timerRef.current);
      if (disposed) return;
      const delay =
        location.pathname === "/play" || isMiniGameActive
          ? GAME_REFRESH_MS
          : FOREGROUND_REFRESH_MS;
      timerRef.current = window.setTimeout(async () => {
        if (document.visibilityState === "visible") await refreshWalletSummary();
        schedule();
      }, delay);
    };
    const refreshWhenActive = () => {
      if (document.visibilityState === "visible") void refreshWalletSummary({ force: true });
    };

    void refreshWalletSummary({ force: true });
    schedule();
    window.addEventListener("focus", refreshWhenActive);
    window.addEventListener("pageshow", refreshWhenActive);
    window.addEventListener("online", refreshWhenActive);
    window.addEventListener(WALLET_SYNC_EVENT, refreshWhenActive);
    window.addEventListener("gameLobbyReturn", refreshWhenActive);
    document.addEventListener("visibilitychange", refreshWhenActive);
    return () => {
      disposed = true;
      window.clearTimeout(timerRef.current);
      window.removeEventListener("focus", refreshWhenActive);
      window.removeEventListener("pageshow", refreshWhenActive);
      window.removeEventListener("online", refreshWhenActive);
      window.removeEventListener(WALLET_SYNC_EVENT, refreshWhenActive);
      window.removeEventListener("gameLobbyReturn", refreshWhenActive);
      document.removeEventListener("visibilitychange", refreshWhenActive);
    };
  }, [isMiniGameActive, location.pathname, refreshWalletSummary, user?.isAuthenticated]);

  const value = useMemo(
    () => ({ walletSummary, refreshWalletSummary, applyWalletSummary }),
    [applyWalletSummary, refreshWalletSummary, walletSummary]
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

export const useWalletSummary = () => {
  const context = useContext(WalletContext);
  if (!context) throw new Error("useWalletSummary must be used within WalletProvider");
  return context;
};
